import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useDeployAuth } from "../../context/DeployAuthContext";
import { DeployBillingCostTrend } from "../../components/deploy/billing/DeployBillingCostTrend";
import DeployLegalFooter from "../../components/deploy/DeployLegalFooter";
import DeployInfraSection from "../../components/deploy/DeployInfraSection";
import McpConnectTabs from "../../components/deploy/McpConnectTabs";
import LimeLogo from "../../assets/lime-logo-no-bg.png";
import { selectedProject, useLimeliteFunnel } from "../../lib/limeliteFunnel";
import { launchProgress, nextLaunchAction } from "../../lib/limeliteOnboarding";

const deployApi = axios.create({
  baseURL: "",
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

/** WCAG 2.1 — visible keyboard focus + minimum target area on small screens (RD-0778). */
const btnFocus =
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-silver-medium/90 focus-visible:ring-offset-2 focus-visible:ring-offset-black min-h-[44px] sm:min-h-0";

function SkeletonBlock({ className }) {
  return <div className={`animate-pulse bg-gray-900 rounded border border-gray-800 ${className}`} />;
}

function isActiveEcommerceProject(project) {
  return (
    (project.coreId === "ecommerce" || project.projectKind === "ecommerce") &&
    !["destroying", "destroyed"].includes(String(project.status || ""))
  );
}

/**
 * Authenticated dashboard — MCP command, keys, projects, EUR billing actions (RD-0047).
 */
function DeployDashboardPage() {
  const { customer, logout, globalKillSwitchActive, refreshSession, deploySupportEmail } =
    useDeployAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const projectType = selectedProject(searchParams.get("project"));
  const track = useLimeliteFunnel();
  const [subscription, setSubscription] = useState(null);
  const [lastPaymentFailedAt, setLastPaymentFailedAt] = useState(null);
  const [projects, setProjects] = useState([]);
  const [projectRefreshError, setProjectRefreshError] = useState("");
  const [copiedLaunchPrompt, setCopiedLaunchPrompt] = useState("");
  const [keys, setKeys] = useState([]);
  const [newKeyOnce, setNewKeyOnce] = useState("");
  const [spend, setSpend] = useState(null);
  const [currentPeriod, setCurrentPeriod] = useState(null);
  const [usage, setUsage] = useState(null);
  const [budgetInput, setBudgetInput] = useState("");
  const [hardCapInput, setHardCapInput] = useState(false);
  const [loadState, setLoadState] = useState({
    sub: true,
    proj: true,
    keys: true,
    spend: true,
    period: true,
    usage: true,
    stripe: true,
  });
  const [actionError, setActionError] = useState("");
  const [checkoutBanner, setCheckoutBanner] = useState("");
  const [emailVerified, setEmailVerified] = useState(customer?.emailVerified ?? null);
  const [verificationSending, setVerificationSending] = useState(false);
  const [verificationNotice, setVerificationNotice] = useState("");
  const verificationSendingRef = useRef(false);
  const verificationRecoveredRef = useRef(false);
  const [statusAvailable, setStatusAvailable] = useState({ subscription: false, projects: false });
  const [mcpConnectedAt, setMcpConnectedAt] = useState(customer?.mcpConnectedAt || null);
  const [abusePatchSlug, setAbusePatchSlug] = useState(null);
  const [abuseAuditEntries, setAbuseAuditEntries] = useState([]);
  const [auditExportLoading, setAuditExportLoading] = useState(false);
  const [zipExportLoading, setZipExportLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("connect");
  const [invoices, setInvoices] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [invoicesFetched, setInvoicesFetched] = useState(false);
  const [cancelConfirm, setCancelConfirm] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  // In-flight guard for Subscribe. A double-click used to fire two POSTs to create-checkout,
  // and the server minted a fresh Stripe Checkout Session for each one.
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [stripeNotice, setStripeNotice] = useState("");
  const [stripeError, setStripeError] = useState("");
  const [stripeConnect, setStripeConnect] = useState(null);
  const [stripeConnectLoading, setStripeConnectLoading] = useState(false);
  const [stripeDisconnectConfirm, setStripeDisconnectConfirm] = useState(false);
  const stripeActionRef = useRef(false);

  useEffect(() => {
    const c = searchParams.get("checkout");
    if (c === "success") {
      setCheckoutBanner("Checkout received. We are confirming your membership.");
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete("checkout");
          next.delete("session_id");
          return next;
        },
        { replace: true }
      );
    } else if (c === "cancel") {
      setCheckoutBanner("Checkout cancelled — you can subscribe anytime from this page.");
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete("checkout");
          return next;
        },
        { replace: true }
      );
    }
  }, [searchParams, setSearchParams]);

  const loadDashboard = useCallback(async () => {
    setLoadState({ sub: true, proj: true, keys: true, spend: true, period: true, usage: true, abuseAudit: true, stripe: true });
    setActionError("");
    const unavailable = (error) => {
      setActionError(error.response?.data?.error || error.message || "Some dashboard information is unavailable.");
      return null;
    };
    try {
      const [subRes, projRes, keysRes, spendRes, periodRes, usageRes, abuseRes, stripeRes, sessionRes] = await Promise.all([
        deployApi.get("/api/deploy/billing/subscription").catch(unavailable),
        deployApi.get("/api/deploy/projects").catch(unavailable),
        deployApi.get("/api/deploy/keys").catch(unavailable),
        deployApi.get("/api/deploy/billing/spend").catch(unavailable),
        deployApi.get("/api/deploy/billing/current-period").catch(() => ({ data: { period: null } })),
        deployApi.get("/api/deploy/billing/usage").catch((e) => {
          console.error(
            `[${new Date().toISOString()}] [DeployDashboardPage] billing/usage failed`,
            e.response?.data || e.message
          );
          return { data: { success: false, error: "usage_unavailable" } };
        }),
        deployApi.get("/api/deploy/audit/abuse-recent?limit=12").catch((e) => {
          console.warn(
            `[${new Date().toISOString()}] [DeployDashboardPage] abuse audit fetch skipped`,
            e.response?.data?.error || e.message
          );
          return { data: { success: false, entries: [] } };
        }),
        deployApi.get("/api/deploy/stripe/connect/status").catch((e) => {
          console.warn(
            `[${new Date().toISOString()}] [DeployDashboardPage] Stripe connection status unavailable`,
            e.response?.data?.error || e.message
          );
          return { data: { success: false, unavailable: true } };
        }),
        deployApi.get("/api/deploy/auth/session").then((response) => {
          const identity = response.data?.customer;
          if (typeof identity?.emailVerified === "boolean") setEmailVerified(identity.emailVerified);
          if (identity) setMcpConnectedAt(identity.mcpConnectedAt || null);
          return response;
        }).catch(() => null),
      ]);
      setStatusAvailable({ subscription: Boolean(subRes), projects: Boolean(projRes) });
      if (sessionRes?.data?.customer) setMcpConnectedAt(sessionRes.data.customer.mcpConnectedAt || null);
      if (subRes) setSubscription(subRes.data.subscription || null);
      if (typeof sessionRes?.data?.customer?.emailVerified !== "boolean" && typeof subRes?.data?.emailVerified === "boolean") setEmailVerified(subRes.data.emailVerified);
      const failedAt = subRes?.data?.deployBillingFlags?.lastPaymentFailedAt ?? null;
      setLastPaymentFailedAt(typeof failedAt === "string" && failedAt ? failedAt : null);
      if (projRes) setProjects(projRes.data.projects || []);
      const fetchedKeys = keysRes?.data?.keys || [];
      if (keysRes) setKeys(fetchedKeys);
      const sp = spendRes?.data;
      if (spendRes) setSpend(sp);
      setCurrentPeriod(periodRes.data?.period || null);
      setUsage(usageRes.data);
      setAbuseAuditEntries(
        Array.isArray(abuseRes.data?.entries) ? abuseRes.data.entries : []
      );
      setStripeConnect(stripeRes.data?.success ? stripeRes.data : { unavailable: true });
      const b = sp?.settings?.monthlyBudgetEur;
      setBudgetInput(b !== null && b !== undefined && !Number.isNaN(Number(b)) ? String(b) : "");
      setHardCapInput(Boolean(sp?.settings?.hardCapAtBudget));
    } catch (e) {
      const msg = e.response?.data?.error || e.message || "Failed to load dashboard";
      setActionError(msg);
      console.error(`[${new Date().toISOString()}] [DeployDashboardPage] load error`, msg);
    } finally {
      setLoadState({ sub: false, proj: false, keys: false, spend: false, period: false, usage: false, abuseAudit: false, stripe: false });
    }
  }, []);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  // MCP can deploy while this dashboard is open. Refresh only the project data
  // on the tabs that use it while visible, including an empty list: new
  // projects and deployments can arrive from the assistant at any time.
  useEffect(() => {
    if (loadState.proj || !["connect", "projects", "payments"].includes(activeTab)) return undefined;
    let cancelled = false;
    let inFlight = false;
    let timer;
    const refresh = async () => {
      clearTimeout(timer);
      if (cancelled || inFlight || document.visibilityState === "hidden") return;
      inFlight = true;
      try {
        const response = await deployApi.get("/api/deploy/projects");
        if (cancelled) return;
        const rows = response.data.projects || [];
        setProjects(rows);
        setProjectRefreshError("");

      } catch (_) {
        if (!cancelled) setProjectRefreshError("Project status could not be refreshed. We will retry automatically.");
      } finally {
        inFlight = false;
        if (!cancelled && document.visibilityState !== "hidden") timer = setTimeout(refresh, 30000);
      }
    };
    const onVisibility = () => {
      clearTimeout(timer);
      if (document.visibilityState !== "hidden") refresh();
    };
    refresh();
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", onVisibility);
    return () => {
      cancelled = true;
      clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", onVisibility);
    };
  }, [activeTab, loadState.proj]);

  useEffect(() => {
    if (!newKeyOnce) return undefined;
    const timer = setTimeout(() => setNewKeyOnce(""), 15 * 60 * 1000);
    return () => clearTimeout(timer);
  }, [newKeyOnce]);

  useEffect(() => {
    const v = searchParams.get("email_verified");
    if (v !== "1") return;
    setCheckoutBanner("Email verified — you can subscribe when ready (EUR).");
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("email_verified");
        return next;
      },
      { replace: true }
    );
    void refreshSession().then(() => loadDashboard());
  }, [searchParams, setSearchParams, refreshSession, loadDashboard]);

  useEffect(() => {
    const outcome = searchParams.get("stripe_connect");
    const requested = searchParams.get("connect") === "stripe";
    if (!outcome && !requested) return;

    setActiveTab("payments");
    if (outcome === "connected") {
      const applyState = searchParams.get("reason");
      const confirmation = {
        redeploy_queued: "Stripe connected and assigned. Limelite is applying it to this Ecommerce project now.",
        applies_on_first_deploy: "Stripe connected and assigned. It will be applied on this project's first deployment.",
        pending: "Stripe connected and assigned, but its configuration still needs to be applied before this store can take payments.",
      };
      setStripeNotice(confirmation[applyState] || "Stripe connected. Choose which Ecommerce project should use this account.");
      void loadDashboard();
    } else if (outcome === "cancelled") {
      setStripeNotice("Stripe connection cancelled — nothing was changed.");
    } else if (outcome === "failed") {
      const reason = searchParams.get("reason");
      setStripeError(
        reason === "STRIPE_CONNECT_CUSTOMER_UNAVAILABLE"
          ? "This account is no longer available to connect Stripe. Sign in again or contact support."
          : reason === "link_expired"
          ? "That Stripe connection link expired. Start again from this dashboard."
          : reason === "open_the_link_yourself"
            ? "Open the Stripe connection from the same browser that started it."
            : "Stripe could not be connected to Limelite. Try again or contact support."
      );
    }

    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("stripe_connect");
        next.delete("reason");
        next.delete("connect");
        return next;
      },
      { replace: true }
    );
    const scrollTimer = window.setTimeout(() => document.getElementById("payments-heading")?.scrollIntoView({ block: "center" }), 0);
    return () => window.clearTimeout(scrollTimer);
  }, [searchParams, setSearchParams, loadDashboard]);

  const resendVerificationEmail = async () => {
    if (verificationSendingRef.current) return;
    verificationSendingRef.current = true;
    setVerificationSending(true);
    setActionError("");
    setVerificationNotice("");
    try {
      const { data } = await deployApi.post("/api/deploy/auth/send-verification-email");
      if (data?.success !== true) throw new Error("Verification email could not be confirmed. Please try again.");
      if (data.emailVerified === true || data.message === "Email already verified.") {
        setEmailVerified(true);
        setVerificationNotice("Your email is already verified.");
      } else if (data.delivery === "sent" || data.message === "Verification email sent.") {
        setVerificationNotice("Verification email sent. Check your inbox and spam folder. Return here after opening the link.");
      } else {
        throw new Error("Email delivery has not been confirmed. Please try again shortly.");
      }
    } catch (e) {
      setActionError(e.response?.status === 429 ? "Please wait a minute before requesting another verification email." : e.response?.data?.error || e.message || "Could not send verification email");
    } finally {
      verificationSendingRef.current = false;
      setVerificationSending(false);
    }
  };

  // Verification may happen in another tab; refresh identity without requiring billing access.
  useEffect(() => {
    if (emailVerified === true) return undefined;
    let cancelled = false;
    let inFlight = false;
    const check = async () => {
      if (inFlight || document.visibilityState === "hidden") return;
      inFlight = true;
      try {
        const { data } = await deployApi.get("/api/deploy/auth/session");
        if (!cancelled && typeof data?.customer?.emailVerified === "boolean") {
          setEmailVerified(data.customer.emailVerified);
          if (data.customer.emailVerified && !verificationRecoveredRef.current) {
            verificationRecoveredRef.current = true;
            await loadDashboard();
            await refreshSession();
          }
        }
      } catch (_) { /* Preserve the recovery controls; the next focus retries. */ }
      finally { inFlight = false; }
    };
    window.addEventListener("focus", check);
    document.addEventListener("visibilitychange", check);
    return () => { cancelled = true; window.removeEventListener("focus", check); document.removeEventListener("visibilitychange", check); };
  }, [emailVerified, loadDashboard, refreshSession]);

  const startCheckout = async () => {
    if (!statusAvailable.subscription) { setActionError("Membership status is unavailable. Refresh before starting checkout."); return; }
    // Re-entrancy guard. React state is async, so the disabled prop alone does not stop a fast
    // double-click from entering this function twice before the first render lands.
    if (checkoutLoading) return;
    setCheckoutLoading(true);
    setActionError("");
    try {
      const { data } = await deployApi.post("/api/deploy/billing/create-checkout");
      if (data.url) {
        track("checkout_started", { source: "dashboard", projectType });
        // Deliberately NOT clearing the flag on success: we are navigating away to Stripe, and
        // re-enabling the button during that redirect just re-opens the double-click window.
        window.location.href = data.url;
        return;
      }
      setActionError("No checkout URL returned");
      setCheckoutLoading(false);
    } catch (e) {
      setActionError(e.response?.data?.error || e.message || "Checkout failed");
      setCheckoutLoading(false);
    }
  };

  const openPortal = async () => {
    try {
      const { data } = await deployApi.post("/api/deploy/billing/create-portal-session");
      if (data.url) window.location.href = data.url;
    } catch (e) {
      setActionError(e.response?.data?.error || e.message || "Could not open billing portal — try again.");
    }
  };

  const startStripeConnect = async (projectSlug = "") => {
    if (stripeActionRef.current) return;
    stripeActionRef.current = true;
    setStripeConnectLoading(true);
    setStripeError("");
    try {
      const { data } = await deployApi.post("/api/deploy/stripe/connect/start", projectSlug ? { projectSlug } : {});
      if (!data?.url) throw new Error("Stripe did not return a connection URL.");
      const target = new URL(data.url);
      if (target.protocol !== "https:" || target.hostname !== "connect.stripe.com") throw new Error("Stripe returned an unexpected connection URL.");
      window.location.assign(target.href);
    } catch (e) {
      stripeActionRef.current = false;
      setStripeConnectLoading(false);
      setStripeError(e.response?.data?.error || e.message || "Could not start the Stripe connection.");
    }
  };

  const assignStripeToProject = async (projectSlug) => {
    if (stripeActionRef.current) return;
    stripeActionRef.current = true;
    setStripeConnectLoading(true);
    setStripeError("");
    try {
      const { data } = await deployApi.post("/api/deploy/stripe/connect/assign", { projectSlug });
      setProjects((current) => current.map((project) => project.slug === projectSlug
        ? {
            ...project,
            stripePayment: {
              assignment: "explicit",
              accountId: data.accountId,
              assignedAt: new Date().toISOString(),
            },
          }
        : project));
      setStripeNotice(
        data.applyState === "redeploy_queued"
          ? `Stripe assigned to ${projectSlug}. Limelite queued the configuration update automatically.`
          : data.applyState === "applies_on_first_deploy"
            ? `Stripe assigned to ${projectSlug}. It will be applied on the first deployment.`
            : `Stripe assigned to ${projectSlug}, but the running store update could not be queued. Apply the assignment again before taking payments.`
      );
    } catch (e) {
      setStripeError(e.response?.data?.error || e.message || "Could not assign Stripe to this project.");
    } finally {
      stripeActionRef.current = false;
      setStripeConnectLoading(false);
    }
  };

  const disconnectStripe = async () => {
    if (!stripeDisconnectConfirm) {
      setStripeDisconnectConfirm(true);
      return;
    }
    if (stripeActionRef.current) return;
    stripeActionRef.current = true;
    setStripeConnectLoading(true);
    setStripeError("");
    try {
      const { data } = await deployApi.post("/api/deploy/stripe/connect/disconnect", { confirmAllProjects: true });
      setStripeConnect((current) => ({ ...(current || {}), connected: false, accountId: "", degraded: "" }));
      setStripeDisconnectConfirm(false);
      setStripeNotice(data?.message || "Stripe disconnected.");
    } catch (e) {
      setStripeError(e.response?.data?.error || e.message || "Could not disconnect Stripe.");
    } finally {
      stripeActionRef.current = false;
      setStripeConnectLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    setCancelLoading(true);
    setActionError("");
    try {
      const { data } = await deployApi.post("/api/deploy/billing/cancel-subscription");
      if (data?.cancelAtPeriodEnd !== true) throw new Error("Cancellation was not confirmed. Please try again.");
      setSubscription(previous => ({ ...previous, cancelAtPeriodEnd: true, currentPeriodEnd: data.currentPeriodEnd || previous?.currentPeriodEnd }));
      setCancelConfirm(false);
      setCheckoutBanner("Subscription cancelled — you keep access until the end of your billing period.");
    } catch (e) {
      setActionError(e.response?.data?.error || e.message || "Cancellation failed — try again.");
    } finally {
      setCancelLoading(false);
    }
  };

  const loadInvoices = useCallback(async () => {
    if (invoicesFetched) return;
    setInvoicesLoading(true);
    try {
      const { data } = await deployApi.get("/api/deploy/billing/invoices");
      setInvoices(data.invoices || []);
      setInvoicesFetched(true);
    } catch {
      setInvoices([]);
      setInvoicesFetched(true);
    } finally {
      setInvoicesLoading(false);
    }
  }, [invoicesFetched]);

  useEffect(() => {
    if (activeTab === "billing") loadInvoices();
  }, [activeTab, loadInvoices]);

  const activeKey = keys.find((k) => !k.revoked) || null;

  const createKey = async (label = "MCP key") => {
    setActionError("");
    setNewKeyOnce("");
    try {
      // No projectSlug: the server mints an ACCOUNT-WIDE key, which works for every project on
      // the account. Sending a slug here would bind the key to one project and force a second
      // key (plus another MCP config edit) for the next one.
      const { data } = await deployApi.post("/api/deploy/keys", {
        label,
        expiresInDays: 90,
      });
      if (data.key) {
        setNewKeyOnce(data.key);
      }
      const list = await deployApi.get("/api/deploy/keys");
      setKeys(list.data.keys || []);
    } catch (e) {
      setActionError(e.response?.data?.error || e.message || "Could not create key");
    }
  };

  const saveSpendSettings = async () => {
    setActionError("");
    try {
      const raw = budgetInput.trim();
      const body = {
        hardCapAtBudget: hardCapInput,
      };
      if (raw === "") body.monthlyBudgetEur = null;
      else body.monthlyBudgetEur = Number(raw);
      const { data } = await deployApi.patch("/api/deploy/billing/spend", body);
      const b = data?.settings?.monthlyBudgetEur;
      setBudgetInput(b !== null && b !== undefined ? String(b) : "");
      setHardCapInput(Boolean(data?.settings?.hardCapAtBudget));
      const r = await deployApi.get("/api/deploy/billing/spend");
      setSpend(r.data);
    } catch (e) {
      setActionError(e.response?.data?.error || e.message || "Could not save spend settings");
    }
  };

  const downloadAuditCsv = async () => {
    setActionError("");
    setAuditExportLoading(true);
    try {
      const to = new Date();
      const from = new Date(to.getTime() - 90 * 24 * 60 * 60 * 1000);
      const params = new URLSearchParams({
        from: from.toISOString(),
        to: to.toISOString(),
      });
      const res = await fetch(`/api/deploy/audit/export.csv?${params.toString()}`, {
        credentials: "include",
      });
      if (!res.ok) {
        const errText = await res.text();
        let msg = `Export failed (${res.status})`;
        try {
          const j = JSON.parse(errText);
          if (j && j.error) msg = String(j.error);
        } catch (_) {
          /* plain text */
        }
        setActionError(msg);
        console.warn(
          `[${new Date().toISOString()}] [DeployDashboardPage] audit CSV error status=${res.status}`
        );
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `limelite-audit-${new Date().toISOString().slice(0, 10)}.csv`;
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setActionError(e.message || "Audit export failed");
      console.error(`[${new Date().toISOString()}] [DeployDashboardPage] audit CSV exception`, e);
    } finally {
      setAuditExportLoading(false);
    }
  };

  const downloadDataExportZip = async () => {
    setActionError("");
    setZipExportLoading(true);
    try {
      const res = await fetch("/api/deploy/privacy/data-export.zip", {
        credentials: "include",
      });
      if (!res.ok) {
        const errText = await res.text();
        let msg = `ZIP export failed (${res.status})`;
        try {
          const j = JSON.parse(errText);
          if (j && j.error) msg = String(j.error);
        } catch (_) {
          /* plain text */
        }
        setActionError(msg);
        console.warn(
          `[${new Date().toISOString()}] [DeployDashboardPage] data-export.zip error status=${res.status}`
        );
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `limelite-data-export-${new Date().toISOString().slice(0, 10)}.zip`;
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setActionError(e.message || "ZIP export failed");
      console.error(`[${new Date().toISOString()}] [DeployDashboardPage] data-export.zip exception`, e);
    } finally {
      setZipExportLoading(false);
    }
  };

  const patchProjectAbuse = async (slug, body) => {
    setActionError("");
    setAbusePatchSlug(slug);
    try {
      await deployApi.patch(`/api/deploy/projects/${encodeURIComponent(slug)}`, body);
      const projRes = await deployApi.get("/api/deploy/projects");
      setProjects(projRes.data.projects || []);
    } catch (e) {
      setActionError(e.response?.data?.error || e.message || "Could not update abuse settings");
    } finally {
      setAbusePatchSlug(null);
    }
  };

  const revokeKey = async (keyId) => {
    setActionError("");
    try {
      await deployApi.delete(`/api/deploy/keys/${keyId}`);
      setNewKeyOnce("");
      try {
        const list = await deployApi.get("/api/deploy/keys");
        setKeys(list.data.keys || []);
      } catch {
        // The key has already been revoked server-side; a failed list refresh
        // must not leave the one-time secret visible or claim otherwise.
      }
    } catch (e) {
      setActionError(e.response?.data?.error || e.message || "Could not revoke key");
    }
  };

  const subActive =
    subscription &&
    ["active", "trialing"].includes(String(subscription.status || "").toLowerCase());
  const launchSteps = launchProgress({ emailVerified, mcpConnectedAt, subscription, projects });
  const connected = launchSteps[1].complete;
  const nextLaunch = emailVerified === true && (!statusAvailable.subscription || !statusAvailable.projects)
    ? { title: "Check your account status", description: "Membership or project status is unavailable. Refresh before starting another subscription or project.", label: "Retry account status", action: "refresh" }
    : nextLaunchAction({ emailVerified, mcpConnectedAt, subscription, projects, projectType });
  const includedAllowance = Math.max(0, Number(currentPeriod?.includedInfraAllowanceEur) || 0);
  const infraUsed = Math.max(0, Number(currentPeriod?.infraUsedEur) || 0);
  const projectedInfra = Math.max(infraUsed, Number(currentPeriod?.projectedInfraEur) || 0);
  const usagePercent = includedAllowance > 0 ? Math.min(100, (infraUsed / includedAllowance) * 100) : 0;
  const projectedPercent = includedAllowance > 0 ? (projectedInfra / includedAllowance) * 100 : 0;
  const usageTone = projectedPercent >= 100 ? "amber" : projectedPercent >= 70 ? "lime" : "green";

  return (
    <>
      <a
        href="#deploy-dashboard-main"
        className="fixed left-4 top-0 z-[100] -translate-y-full focus:translate-y-0 transition-transform px-4 py-3 bg-slate-900 border border-silver-medium border-t-0 rounded-b-md text-silver-light shadow-lg outline-none focus-visible:ring-2 focus-visible:ring-silver-medium"
      >
        Skip to main content
      </a>
      <main
        id="deploy-dashboard-main"
        tabIndex={-1}
        className="bg-black text-silver-light min-h-[calc(100vh-4rem)] px-4 sm:px-6 py-8 sm:py-10 outline-none"
      >
      <div className="max-w-5xl mx-auto space-y-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-3xl font-serif font-medium">Deploy dashboard</h1>
              {subscription?.planTier === "enterprise" && (
                <span
                  className="text-xs font-medium uppercase tracking-wide border border-amber-700/80 text-amber-100 bg-amber-950/50 px-2 py-0.5 rounded"
                  title="Enterprise — see docs/deploy/enterprise-tier.md for SLA and support"
                >
                  Enterprise
                </span>
              )}
            </div>
            <p className="text-silver-medium text-sm mt-1">
              Signed in as <span className="text-silver-light">{customer?.email}</span>
            </p>
          </div>
          <div className="flex gap-3 items-center">
            <Link
              to="/limelite/deploy"
              className={`text-sm text-silver-medium hover:text-silver-light underline rounded px-1 py-2 ${btnFocus}`}
            >
              Product page
            </Link>
            <Link
              to="/limelite/deploy/account"
              className={`text-sm border border-gray-700 px-4 py-2 rounded-md hover:border-silver-medium ${btnFocus}`}
            >
              Account
            </Link>
            <button
              type="button"
              onClick={() => logout()}
              className={`text-sm border border-gray-700 px-4 py-2 rounded-md hover:border-silver-medium ${btnFocus}`}
            >
              Log out
            </button>
          </div>
        </div>

        {stripeNotice ? <p role="status" className="mb-4 text-sm text-lime-200">{stripeNotice}</p> : null}
        {stripeError ? <p role="alert" className="mb-4 text-sm text-red-300">{stripeError}</p> : null}
        {checkoutBanner && (!subActive || subscription?.cancelAtPeriodEnd) && (
          <div className="bg-gray-900 border border-gray-700 rounded-lg px-4 py-3 text-sm text-silver-light">
            {checkoutBanner}
          </div>
        )}

        {verificationNotice && <p role="status" className="text-sm text-lime-300">{verificationNotice}</p>}

        {emailVerified === false && (
          <div
            className="flex flex-wrap items-center justify-between gap-3 bg-gray-900/70 border border-gray-800 rounded-lg px-4 py-2.5 text-sm text-silver-medium"
            role="status"
          >
            <p>
              <span className="font-medium text-silver-light">Email verification pending</span>
              <span className="text-silver-dark"> · You can connect now. Verify before subscribing or deploying.</span>
            </p>
            <button
              type="button"
              onClick={resendVerificationEmail}
              disabled={verificationSending}
              className={`text-sm text-silver-light hover:text-lime-400 underline underline-offset-4 rounded px-1 py-1 ${btnFocus}`}
            >
              {verificationSending ? "Sending…" : "Resend link"}
            </button>
          </div>
        )}

        {globalKillSwitchActive && (
          <div
            className="bg-slate-900/80 border border-slate-600 rounded-lg px-4 py-3 text-sm text-slate-100"
            role="status"
          >
            <p className="font-medium text-slate-50">Provisioning paused (operator)</p>
            <p className="text-slate-200/90 mt-1">
              New MCP deploys and Phase 2 domain / preview creates are temporarily disabled. Restarts, logs, destroy,
              rollback, and redeploy still run. This clears when RIVIERADEPLOY_GLOBAL_KILL_SWITCH is turned off.
            </p>
          </div>
        )}

        {lastPaymentFailedAt && (
          <div
            className="bg-amber-950/40 border border-amber-800/80 rounded-lg px-4 py-3 text-sm text-amber-100"
            role="status"
          >
            <p className="font-medium text-amber-50">Subscription payment issue (EUR)</p>
            <p className="text-amber-100/90 mt-1">
              A recent invoice payment did not go through. Update your payment method in the Customer portal to avoid
              service interruption. Last event: {new Date(lastPaymentFailedAt).toLocaleString()}
            </p>
            <button
              type="button"
              onClick={openPortal}
              className="mt-3 text-sm border border-amber-700/80 hover:border-amber-500 text-amber-50 px-3 py-1.5 rounded"
            >
              Open billing portal
            </button>
          </div>
        )}

        {actionError && (
          <div className="bg-red-950/50 border border-red-900 rounded-lg px-4 py-3 text-sm text-red-200">
            {actionError}
          </div>
        )}

        {projectRefreshError && ["connect", "projects", "payments"].includes(activeTab) && (
          <p role="alert" className="text-sm text-amber-200">{projectRefreshError}</p>
        )}

        <nav className="flex border-b border-gray-800" aria-label="Dashboard navigation">
          {[
            { id: "connect", label: "AI setup" },
            { id: "projects", label: "Projects" },
            { id: "payments", label: "Store payments" },
            { id: "usage", label: "Usage" },
            { id: "billing", label: "Billing" },
            { id: "security", label: "Security" },
          ].map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 py-3 text-sm font-medium whitespace-nowrap -mb-px border-b-2 transition-colors ${btnFocus} ${
                activeTab === tab.id
                  ? "text-silver-light border-silver-light"
                  : "text-silver-medium border-transparent hover:text-silver-light hover:border-gray-600"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>

        <div className="space-y-6">

          {activeTab === "connect" && (
          <section className="bg-off-black border border-gray-800 rounded-lg p-4 sm:p-6" aria-labelledby="mcp-setup-heading">
          <div className="mb-7 rounded-xl border border-gray-800 bg-black/40 p-4">
            <div className="flex flex-wrap items-baseline justify-between gap-2 mb-4">
              <div>
                <p className="text-xs uppercase tracking-[0.18em] text-lime-400">Your first Limelite launch</p>
                <p className="text-sm text-silver-medium mt-1">Follow your next step below. You can explore with your assistant before subscribing.</p>
              </div>
              <span className="text-xs text-silver-dark">{launchSteps.filter((step) => step.complete).length} of {launchSteps.length} complete</span>
            </div>
            <ol className="grid grid-cols-2 sm:grid-cols-5 gap-2" aria-label="First launch progress">
              {launchSteps.map((step, index) => {
                const next = !step.complete && launchSteps.slice(0, index).every((previous) => previous.complete);
                return (
                  <li
                    key={step.label}
                    className={`rounded-md border px-3 py-2 text-xs ${
                      step.complete
                        ? "border-lime-900/70 bg-lime-950/30 text-lime-300"
                        : next
                          ? "border-lime-600/80 bg-lime-950/20 text-silver-light"
                          : "border-gray-800 bg-gray-900/40 text-silver-dark"
                    }`}
                  >
                    <span className="mr-1.5 font-semibold" aria-hidden="true">{step.complete ? "✓" : index + 1}</span>
                    {step.label}
                  </li>
                );
              })}
            </ol>
            {!loadState.sub && !loadState.proj && (
              <div className="mt-5 border-t border-gray-800 pt-4" aria-labelledby="next-launch-heading">
                <h3 id="next-launch-heading" className="text-lg text-silver-light font-medium">{nextLaunch.title}</h3>
                <p className="mt-2 text-sm text-silver-medium">{nextLaunch.description}</p>
                {nextLaunch.prompt && <p className="mt-3 rounded border border-gray-700 bg-black/40 p-3 text-sm text-silver-light" aria-label="Launch request">{nextLaunch.prompt}</p>}
                {nextLaunch.to ? (
                  <Link to={nextLaunch.to} className={`inline-block mt-4 rounded bg-lime-400 px-4 py-2 text-black font-medium ${btnFocus}`}>{nextLaunch.label}</Link>
                ) : (
                  <button type="button" disabled={nextLaunch.action === "verify" && verificationSending} className={`mt-4 rounded bg-lime-400 px-4 py-2 text-black font-medium ${btnFocus}`} onClick={async () => {
                    if (nextLaunch.action === "refresh") loadDashboard();
                    else if (nextLaunch.action === "verify") resendVerificationEmail();
                    else if (nextLaunch.action === "copyPrompt") {
                      try { await navigator.clipboard.writeText(nextLaunch.prompt); setCopiedLaunchPrompt(nextLaunch.prompt); }
                      catch (_) { setActionError("Could not copy automatically. Select and copy the launch request above."); }
                    }
                    else setActiveTab(nextLaunch.action);
                  }}>{nextLaunch.label}</button>
                )}
                {nextLaunch.prompt && copiedLaunchPrompt === nextLaunch.prompt && <p role="status" className="mt-2 text-sm text-lime-300">Copied. Paste the request into your AI tool to continue.</p>}
              </div>
            )}
          </div>
          <h2 id="mcp-setup-heading" className="text-xl font-medium mb-1">
            {connected ? "Your AI connection" : "Connect your AI assistant for free"}
          </h2>
          <p className="text-silver-medium text-sm mb-4">
            {newKeyOnce
              ? "Copy this into your AI tool — your project key is already included."
              : activeKey ? "Use the key you saved when creating it. If you no longer have it, create another key below; existing keys stay valid." : "Create a key, then connect your MCP-compatible AI assistant. No subscription is needed to explore."}
          </p>

          {/* Connect snippet — tabbed per AI tool, key pre-filled when a new key was just created */}
          {loadState.keys ? (
            <SkeletonBlock className="h-40 w-full mb-6" />
          ) : (
            <McpConnectTabs apiKey={newKeyOnce || null} className="mb-6" />
          )}

          {/* Subscription state is an onboarding step, not an error state. */}
          {!statusAvailable.subscription ? <p role="status">Membership status is unavailable. Use Retry account status above.</p> : !subActive ? (
            <div className="rounded-lg border border-lime-800/70 bg-gradient-to-r from-lime-950/40 to-gray-900/50 p-4 mb-4">
              <svg
                xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"
                fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                className="text-lime-400 shrink-0" aria-hidden="true"
              >
                <rect width="18" height="11" x="3" y="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
              <div className="min-w-0 flex-1">
                <p className="text-silver-light text-sm font-medium leading-tight">Explore free. Subscribe when you are ready to launch.</p>
                <p className="text-silver-medium text-sm leading-relaxed mt-1">
                  Ask your assistant to show the available projects. Paid deployment stays locked until you subscribe.
                </p>
                <p className="text-silver-medium text-xs leading-relaxed mt-2">
                  Membership is €29/month with €15 of monthly infrastructure allowance. Your first Ecommerce project adds €20/month: €49 total, with €25 total allowance. Usage above the allowance is billed separately; your AI tool subscription is separate.
                </p>
              </div>
              <button
                type="button"
                onClick={startCheckout}
                disabled={checkoutLoading}
                aria-busy={checkoutLoading}
                className={`mt-4 bg-lime-600 hover:bg-lime-500 text-black font-semibold text-sm px-4 py-2 rounded transition-colors disabled:opacity-60 disabled:cursor-not-allowed ${btnFocus}`}
              >
                {checkoutLoading ? "Opening…" : "Start membership — €29/month"}
              </button>
              <p className="mt-3 text-xs text-silver-medium">This checkout starts base membership. Ecommerce requires a separate €20/month project entitlement before launch. <Link className="text-lime-400 underline" to={`/limelite/pilot?project=${projectType || "ecommerce"}`}>Request an assisted launch</Link> if you would like us to agree the setup first.</p>
            </div>
          ) : (
            <div className="relative overflow-hidden rounded-xl border border-lime-800/70 bg-gradient-to-br from-lime-950/50 via-gray-950 to-black p-5 mb-5">
              <div className="absolute -right-10 -top-10 h-32 w-32 rounded-full bg-lime-400/10 blur-3xl" aria-hidden="true" />
              <div className="relative flex items-start gap-4">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-lime-700/70 bg-black/40 shadow-lg shadow-lime-950/40">
                  <img src={LimeLogo} alt="Limelite" className="h-8 w-8" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-medium uppercase tracking-[0.18em] text-lime-400">Limelite toolkit active</p>
                  <h3 className="mt-1 text-lg font-medium text-silver-light">Your membership is active.</h3>
                  <p className="mt-1 text-sm leading-relaxed text-silver-medium">
                    Choose a project and review its requirements with your assistant. Premium projects need their own entitlement before deployment.
                  </p>
                  <div className="mt-4 flex flex-wrap gap-2 text-xs">
                    <span className="rounded-full border border-lime-800/70 bg-lime-950/40 px-2.5 py-1 text-lime-200">MCP deploys active</span>
                    <span className="rounded-full border border-gray-700 bg-black/30 px-2.5 py-1 text-silver-medium">Ecommerce +€20/month per project</span>
                  </div>
                  <p className="mt-4 text-sm text-silver-light">
                    {connected ? "Your assistant is connected. Follow your next launch step above." : activeKey ? "Connect using your saved key, or create another key below if you no longer have it." : "Create your project key below to connect your assistant."}
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-gray-700 p-4" role="status">
            <p className="text-sm text-silver-medium">{connected ? "MCP connection confirmed. Ask your assistant to list the available projects and explain the costs." : "Waiting for your assistant to connect. Use the command with your saved or newly created key, then check your connection."}</p>
            <button type="button" onClick={loadDashboard} className={`rounded border border-gray-600 px-3 py-2 text-sm ${btnFocus}`}>Check connection and progress</button>
          </div>

          {/* A signed-in customer can create a key before subscribing; the MCP subscription gate activates it for deploys. */}
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => createKey("MCP key")}
              className={`bg-black border border-silver-medium hover:bg-silver-medium hover:text-black text-silver-light px-4 py-2 rounded-md text-sm ${btnFocus}`}
            >
              {activeKey ? "Create another key" : "Create your project key"}
            </button>
            {activeKey ? (
              <button
                type="button"
                onClick={() => revokeKey(activeKey.keyId)}
                className={`text-red-400 hover:text-red-300 text-sm px-3 py-2 rounded ${btnFocus}`}
              >
                Revoke {activeKey.keyPrefix}
              </button>
            ) : null}
          </div>

          {subActive && activeKey && projects.length === 0 && !loadState.proj ? (
            <div className="mt-6 rounded-xl border border-lime-900/60 bg-lime-950/15 p-4">
              <p className="text-xs uppercase tracking-[0.18em] text-lime-400">Next up</p>
              <h3 className="text-lg font-medium text-silver-light mt-1">Choose your first launch</h3>
              <p className="text-sm text-silver-medium mt-2 max-w-2xl">
                In your connected AI tool, start from a Riviera solution template or deploy a repository you already own.
              </p>
              <div className="flex flex-wrap gap-3 mt-4">
                <Link to="/limelite/projects" className={`text-sm font-medium text-lime-400 hover:text-lime-300 underline underline-offset-4 rounded px-1 py-1 ${btnFocus}`}>
                  Explore starting points
                </Link>
                <Link to="/limelite/deploy/setup" className={`text-sm text-silver-medium hover:text-silver-light underline underline-offset-4 rounded px-1 py-1 ${btnFocus}`}>
                  Open setup guide
                </Link>
              </div>
            </div>
          ) : null}

          {actionError ? <p className="text-red-400 text-sm mt-3">{actionError}</p> : null}
        </section>
          )}

          {/* Billing — subscription + invoices */}
          {activeTab === "payments" && (
            <section className="bg-off-black border border-gray-800 rounded-lg p-4 sm:p-6" aria-labelledby="payments-heading">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                <div>
                  <p className="text-xs uppercase tracking-[0.18em] text-lime-400">Ecommerce projects</p>
                  <h2 id="payments-heading" className="mt-1 text-xl font-medium text-silver-light">Store payments</h2>
                  <p className="mt-2 max-w-2xl text-sm text-silver-medium">
                    Choose where each store gets paid. Authorization belongs to your Limelite account;
                    money routing is assigned separately to each Ecommerce project.
                  </p>
                </div>
                {!loadState.stripe && stripeConnect?.connected ? (
                  <span className="shrink-0 rounded-full border border-lime-700 bg-lime-950/40 px-2.5 py-1 text-xs text-lime-300">
                    Stripe connected · {stripeConnect.livemode ? "Live" : "Sandbox"}
                  </span>
                ) : null}
              </div>

              {!loadState.stripe && stripeConnect?.unavailable ? (
                <p className="mt-4 text-sm text-amber-200">Stripe connection status is temporarily unavailable. Try again shortly.</p>
              ) : !loadState.stripe && stripeConnect?.degraded ? (
                <div className="mt-4 rounded-md border border-amber-800/70 bg-amber-950/30 p-3 text-sm text-amber-100">
                  This Stripe connection needs attention. Reconnect it from a project below before deploying or taking payments.
                </div>
              ) : !loadState.stripe && stripeConnect?.connected ? (
                <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-gray-800 bg-black/20 p-3">
                  <p className="text-xs text-silver-medium">
                    Authorized account <span className="font-mono text-silver-light">{stripeConnect.accountId}</span>
                  </p>
                  <div className="flex flex-wrap items-center gap-2">
                    {stripeDisconnectConfirm ? (
                      <button
                        type="button"
                        onClick={() => setStripeDisconnectConfirm(false)}
                        className={`text-xs text-silver-medium hover:text-silver-light px-3 py-2 rounded ${btnFocus}`}
                      >
                        Keep connected
                      </button>
                    ) : null}
                    <button
                      type="button"
                      onClick={disconnectStripe}
                      disabled={stripeConnectLoading}
                      className={`text-xs border px-3 py-2 rounded disabled:opacity-60 ${stripeDisconnectConfirm ? "border-red-700 text-red-300 hover:border-red-500" : "border-gray-700 text-silver-medium hover:text-silver-light"} ${btnFocus}`}
                    >
                      {stripeConnectLoading ? "Disconnecting…" : stripeDisconnectConfirm ? "Confirm disconnect" : "Disconnect account"}
                    </button>
                  </div>
                  {stripeDisconnectConfirm ? (
                    <p className="basis-full text-xs text-red-300">
                      This affects every assigned active Ecommerce project: {projects
                        .filter((project) => isActiveEcommerceProject(project) && project.stripePayment?.assignment !== "none")
                        .map((project) => project.displayName || project.slug)
                        .join(", ") || "none"}. Deployed stores stop taking payments immediately.
                    </p>
                  ) : null}
                </div>
              ) : null}

              {loadState.proj || loadState.stripe ? (
                <SkeletonBlock className="h-32 w-full mt-5" />
              ) : projects.filter(isActiveEcommerceProject).length === 0 ? (
                <div className="mt-5 rounded-lg border border-gray-800 bg-black/30 p-4">
                  <p className="text-sm text-silver-light">Create an Ecommerce project first.</p>
                  <p className="mt-1 text-xs text-silver-dark">Its Store payments card will appear here before the first deployment.</p>
                </div>
              ) : (
                <div className="mt-5 space-y-3">
                  {projects
                    .filter(isActiveEcommerceProject)
                    .map((project) => {
                      const payment = project.stripePayment || {};
                      const explicitAssignment = payment.assignment === "explicit";
                      const assigned = Boolean(
                        stripeConnect?.connected &&
                        payment.assignment !== "none" &&
                        (payment.assignment !== "explicit" || payment.accountId === stripeConnect.accountId)
                      );
                      return (
                        <article key={project.slug} className="rounded-lg border border-gray-800 bg-black/30 p-4">
                          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                            <div className="min-w-0">
                              <h3 className="font-medium text-silver-light">{project.displayName || project.slug}</h3>
                              <p className="mt-1 text-xs text-silver-dark">Project <span className="font-mono">{project.slug}</span> · {project.status}</p>
                              {assigned ? (
                                <p className="mt-2 text-sm text-lime-300">
                                  Paid into <span className="font-mono">{stripeConnect.accountId}</span> · {stripeConnect.livemode ? "Live" : "Sandbox"}
                                  {!explicitAssignment ? " · inherited during migration" : ""}
                                </p>
                              ) : (
                                <p className="mt-2 text-sm text-amber-200">Payments are not assigned to this project.</p>
                              )}
                            </div>
                            {assigned && explicitAssignment ? (
                              <span className="rounded-full border border-lime-800 bg-lime-950/30 px-3 py-1.5 text-xs text-lime-300">Assigned</span>
                            ) : assigned ? (
                              <button
                                type="button"
                                onClick={() => assignStripeToProject(project.slug)}
                                disabled={stripeConnectLoading}
                                className={`rounded border border-lime-700 px-4 py-2 text-sm font-medium text-lime-300 hover:bg-lime-950/30 disabled:opacity-60 ${btnFocus}`}
                              >
                                Confirm for this project
                              </button>
                            ) : stripeConnect?.connected ? (
                              <button
                                type="button"
                                onClick={() => assignStripeToProject(project.slug)}
                                disabled={stripeConnectLoading}
                                className={`rounded bg-lime-600 px-4 py-2 text-sm font-semibold text-black hover:bg-lime-500 disabled:opacity-60 ${btnFocus}`}
                              >
                                {stripeConnectLoading ? "Applying…" : "Use connected Stripe account"}
                              </button>
                            ) : stripeConnect?.configured ? (
                              <button
                                type="button"
                                onClick={() => startStripeConnect(project.slug)}
                                disabled={stripeConnectLoading}
                                className={`rounded bg-lime-600 px-4 py-2 text-sm font-semibold text-black hover:bg-lime-500 disabled:opacity-60 ${btnFocus}`}
                              >
                                {stripeConnectLoading ? "Opening Stripe…" : "Connect Stripe for this project"}
                              </button>
                            ) : (
                              <span className="text-xs text-amber-200">Stripe connections are not available yet. Request an assisted launch before accepting store payments.</span>
                            )}
                          </div>
                        </article>
                      );
                    })}
                </div>
              )}

              <div className="mt-5 rounded-lg border border-gray-800 bg-black/20 p-3 text-xs text-silver-medium">
                Limelite never asks you to paste a Stripe secret. Connection approval happens on Stripe&apos;s website,
                and a changed assignment is applied automatically to an already deployed store.
              </div>
            </section>
          )}

          {/* Billing — subscription + invoices */}
          {activeTab === "billing" && (
          <div className="space-y-5">

            {/* Subscription card */}
            <section className="bg-off-black border border-gray-800 rounded-lg p-6">
              <h2 className="text-xl font-medium mb-4">Subscription</h2>
              {loadState.sub ? (
                <SkeletonBlock className="h-16 w-full" />
              ) : (
                <div className="space-y-4 text-sm">
                  <div className="flex flex-wrap items-center gap-4">
                    <div>
                      <p className="text-silver-dark text-xs uppercase tracking-wider mb-1">Plan</p>
                      <p className="text-silver-light font-medium">
                        Limelite Deploy {subscription?.planTier === "enterprise" ? "— Enterprise" : "— Standard"}
                      </p>
                    </div>
                    <div>
                      <p className="text-silver-dark text-xs uppercase tracking-wider mb-1">Status</p>
                      <span className={`text-xs border rounded px-2 py-0.5 ${
                        subActive
                          ? "text-lime-300 border-lime-700 bg-lime-900/20"
                          : "text-silver-dark border-gray-700"
                      }`}>
                        {!statusAvailable.subscription ? "Unavailable" : subActive ? (subscription?.status === "trialing" ? "Trial" : "Active") : "Inactive"}
                      </span>
                    </div>
                    {subscription?.currentPeriodEnd && (
                      <div>
                        <p className="text-silver-dark text-xs uppercase tracking-wider mb-1">{subscription?.cancelAtPeriodEnd ? "Access until" : "Renews"}</p>
                        <p className="text-silver-medium">{new Date(subscription.currentPeriodEnd).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</p>
                      </div>
                    )}
                    <div>
                      <p className="text-silver-dark text-xs uppercase tracking-wider mb-1">Base price</p>
                      <p className="text-silver-medium">€29 / month + usage</p>
                    </div>
                  </div>
                  {subscription?.cancelAtPeriodEnd && subActive && (
                    <p role="status" className="text-sm text-amber-200">Cancellation scheduled. Your membership will not renew; access continues until the end of your paid period.</p>
                  )}
                  {statusAvailable.subscription && !subActive && (
                    <p className="text-silver-dark text-sm border border-gray-800 rounded-lg p-3 bg-gray-900/30">
                      No active subscription. Subscribe below to enable MCP deploy automation.
                    </p>
                  )}
                  <div className="flex flex-wrap gap-3 pt-1">
                    {statusAvailable.subscription && !subActive && (
                      <button
                        type="button"
                        onClick={startCheckout}
                        disabled={checkoutLoading}
                        aria-busy={checkoutLoading}
                        className={`bg-lime-600 hover:bg-lime-500 text-black font-semibold px-4 py-2 rounded-md text-sm transition-colors disabled:opacity-60 disabled:cursor-not-allowed ${btnFocus}`}
                      >
                        {checkoutLoading ? "Opening checkout…" : "Subscribe — €29/month"}
                      </button>
                    )}
                    {subActive && !subscription?.cancelAtPeriodEnd && !cancelConfirm && (
                      <button
                        type="button"
                        onClick={() => setCancelConfirm(true)}
                        className={`border border-red-900/60 hover:border-red-700 text-red-400 hover:text-red-300 px-4 py-2 rounded-md text-sm transition-colors ${btnFocus}`}
                      >
                        Cancel subscription
                      </button>
                    )}
                    {subActive && !subscription?.cancelAtPeriodEnd && cancelConfirm && (
                      <div className="flex items-center gap-3 border border-red-900/60 rounded-md px-4 py-2 bg-red-900/10">
                        <span className="text-sm text-silver-medium">Cancel at period end?</span>
                        <button
                          type="button"
                          onClick={handleCancelSubscription}
                          disabled={cancelLoading}
                          className={`text-sm text-red-400 hover:text-red-300 font-medium disabled:opacity-50 ${btnFocus}`}
                        >
                          {cancelLoading ? "Cancelling…" : "Yes, cancel"}
                        </button>
                        <button
                          type="button"
                          onClick={() => setCancelConfirm(false)}
                          disabled={cancelLoading}
                          className={`text-sm text-silver-dark hover:text-silver-medium disabled:opacity-50 ${btnFocus}`}
                        >
                          Never mind
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </section>

            {/* Invoice list */}
            <section className="bg-off-black border border-gray-800 rounded-lg p-6">
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-xl font-medium">Invoices</h2>
                {invoices.length > 0 && (
                  <a
                    href="/api/deploy/billing/invoices/export.csv"
                    download
                    className={`text-sm border border-gray-700 hover:border-gray-500 text-silver-dark hover:text-silver-light px-3 py-1.5 rounded-md transition-colors ${btnFocus}`}
                  >
                    Export CSV
                  </a>
                )}
              </div>

              {invoicesLoading ? (
                <div className="space-y-2">
                  {[1,2,3].map(i => <SkeletonBlock key={i} className="h-10 w-full" />)}
                </div>
              ) : invoices.length === 0 ? (
                <p className="text-silver-dark text-sm">
                  {subActive ? "No invoices yet — your first one will appear here after the billing period ends." : "Subscribe to generate invoices."}
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-800 text-left">
                        <th className="pb-3 text-silver-dark text-xs uppercase tracking-wider font-medium pr-4">Invoice</th>
                        <th className="pb-3 text-silver-dark text-xs uppercase tracking-wider font-medium pr-4">Date</th>
                        <th className="pb-3 text-silver-dark text-xs uppercase tracking-wider font-medium pr-4">Period</th>
                        <th className="pb-3 text-silver-dark text-xs uppercase tracking-wider font-medium pr-4">Amount</th>
                        <th className="pb-3 text-silver-dark text-xs uppercase tracking-wider font-medium pr-4">Status</th>
                        <th className="pb-3 text-silver-dark text-xs uppercase tracking-wider font-medium text-right">PDF</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoices.map((inv) => {
                        const isPaid = inv.status === "paid";
                        const isOpen = inv.status === "open";
                        return (
                          <tr key={inv.id} className="border-b border-gray-900 hover:bg-gray-900/30 transition-colors">
                            <td className="py-3 pr-4 font-mono text-xs text-silver-medium">{inv.number || inv.id}</td>
                            <td className="py-3 pr-4 text-silver-medium">
                              {inv.created ? new Date(inv.created * 1000).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—"}
                            </td>
                            <td className="py-3 pr-4 text-silver-dark text-xs">
                              {inv.period_start && inv.period_end
                                ? `${new Date(inv.period_start * 1000).toLocaleDateString("en-GB", { day: "numeric", month: "short" })} – ${new Date(inv.period_end * 1000).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}`
                                : "—"}
                            </td>
                            <td className="py-3 pr-4 font-medium text-silver-light">
                              €{(((inv.amount_paid || inv.amount_due) || 0) / 100).toFixed(2)}
                            </td>
                            <td className="py-3 pr-4">
                              <span className={`text-xs border rounded px-2 py-0.5 ${
                                isPaid ? "text-lime-300 border-lime-700 bg-lime-900/20" :
                                isOpen ? "text-amber-300 border-amber-700 bg-amber-900/20" :
                                "text-silver-dark border-gray-700"
                              }`}>
                                {inv.status}
                              </span>
                            </td>
                            <td className="py-3 text-right">
                              {inv.invoice_pdf ? (
                                <a
                                  href={inv.invoice_pdf}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-xs text-lime-600 hover:text-lime-400 transition-colors"
                                >
                                  Download PDF
                                </a>
                              ) : (
                                <span className="text-silver-dark text-xs">—</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

          </div>
          )}

          {/* Usage — infra spend + cost trend */}
          {activeTab === "usage" && (
          <>
          <section className="bg-off-black border border-gray-800 rounded-lg p-6">
          <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
            <div>
              <h2 className="text-xl font-medium">Monthly infrastructure usage</h2>
              <p className="text-silver-medium text-sm mt-1">Your included allowance and spend controls, in one place.</p>
            </div>
            {currentPeriod?.monthKey ? <span className="text-xs text-silver-dark border border-gray-800 rounded-full px-2.5 py-1">{currentPeriod.monthKey} · EUR</span> : null}
          </div>
          {loadState.spend || loadState.period ? (
            <SkeletonBlock className="h-64 w-full" />
          ) : spend && currentPeriod ? (
            <div className="space-y-6">
              <div className="rounded-xl border border-gray-800 bg-black/40 p-5">
                <div className="flex flex-wrap items-end justify-between gap-3">
                  <div>
                    <p className="text-sm text-silver-medium">Usage this month</p>
                    <p className="mt-1 text-3xl font-semibold text-silver-light">{usagePercent.toFixed(0)}<span className="text-xl">%</span></p>
                  </div>
                  <span className={`rounded-full border px-3 py-1 text-xs font-medium ${
                    usageTone === "amber"
                      ? "border-amber-700/70 bg-amber-950/30 text-amber-200"
                      : usageTone === "lime"
                        ? "border-lime-800/70 bg-lime-950/30 text-lime-200"
                        : "border-emerald-800/70 bg-emerald-950/30 text-emerald-200"
                  }`}>
                    {usageTone === "amber" ? "May exceed 100%" : usageTone === "lime" ? "Approaching 100%" : "On track"}
                  </span>
                </div>
                <div className="mt-5">
                  <div className="mb-2 flex justify-between text-xs text-silver-dark">
                    <span>0%</span>
                    <span>100%</span>
                  </div>
                  <div
                    className="relative h-3 overflow-hidden rounded-full bg-gray-900"
                    role="progressbar"
                    aria-label="Monthly infrastructure usage"
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-valuenow={usagePercent}
                  >
                    <div
                      className={usageTone === "amber" ? "h-full bg-amber-500" : usageTone === "lime" ? "h-full bg-lime-500" : "h-full bg-emerald-500"}
                      style={{ width: `${usagePercent}%` }}
                    />
                    {includedAllowance > 0 && projectedPercent > usagePercent ? (
                      <span
                        className="absolute top-0 h-full w-0.5 bg-silver-light/90"
                        style={{ left: `${Math.min(100, projectedPercent)}%` }}
                        title={`Projected month-end usage: ${Math.round(projectedPercent)}%`}
                      />
                    ) : null}
                  </div>
                  <p className="mt-3 text-sm text-silver-medium">
                    {projectedPercent >= 100
                      ? `At this pace, usage may reach ${Math.round(projectedPercent)}% this month.`
                      : `At this pace, usage is expected to finish around ${Math.round(projectedPercent)}% this month.`}
                  </p>
                </div>
              </div>

              <div className="grid gap-3 sm:grid-cols-3">
                <div className="rounded-lg border border-gray-800 bg-black/30 p-4">
                  <p className="text-xs uppercase tracking-wider text-silver-dark">Used so far</p>
                  <p className="mt-1 text-lg font-medium text-silver-light">{usagePercent.toFixed(0)}%</p>
                  <p className="mt-1 text-xs text-silver-dark">Current monthly usage</p>
                </div>
                <div className="rounded-lg border border-gray-800 bg-black/30 p-4">
                  <p className="text-xs uppercase tracking-wider text-silver-dark">Projected month-end</p>
                  <p className="mt-1 text-lg font-medium text-silver-light">{Math.round(projectedPercent)}%</p>
                  <p className="mt-1 text-xs text-silver-dark">Based on current resources</p>
                </div>
                <div className="rounded-lg border border-gray-800 bg-black/30 p-4">
                  <p className="text-xs uppercase tracking-wider text-silver-dark">Estimate status</p>
                  <p className="mt-1 text-lg font-medium text-silver-light capitalize">{currentPeriod.status || "estimated"}</p>
                  <p className="mt-1 text-xs text-silver-dark">{currentPeriod.source || spend.snapshot?.estimateSource || "estimate"}</p>
                </div>
              </div>

              <div className="border border-gray-800 rounded-xl bg-black/20 p-4 space-y-3">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <div>
                    <h3 className="font-medium text-silver-light">Spend control</h3>
                    <p className="text-sm text-silver-medium mt-1">Set a monthly safety limit for new deploys.</p>
                  </div>
                  <span className="text-xs text-silver-dark">{budgetInput ? `Safety limit: €${Number(budgetInput).toFixed(2)}` : "No safety limit set"}</span>
                </div>
                <label className="block text-silver-medium">
                  Monthly safety limit (EUR, optional)
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={budgetInput}
                    onChange={(e) => setBudgetInput(e.target.value)}
                    className="mt-1 w-full bg-black border border-gray-700 rounded px-3 py-2 text-silver-light"
                    placeholder="e.g. 150"
                  />
                </label>
                <label className="flex items-center gap-2 text-silver-medium cursor-pointer">
                  <input
                    type="checkbox"
                    checked={hardCapInput}
                    onChange={(e) => setHardCapInput(e.target.checked)}
                  />
                  Hard cap — block new MCP deploys when month-to-date ≥ budget
                </label>
                <button
                  type="button"
                  onClick={saveSpendSettings}
                  className={`bg-black border border-silver-medium hover:bg-silver-medium hover:text-black text-silver-light px-4 py-2 rounded-md text-sm ${btnFocus}`}
                >
                  Save budget settings
                </button>
              </div>
            </div>
          ) : (
            <p className="text-silver-medium text-sm">Could not load spend snapshot.</p>
          )}
        </section>

          <DeployBillingCostTrend usage={usage} loading={loadState.usage} />
          <DeployInfraSection />
          </>
          )}

          {/* Projects */}
          {activeTab === "projects" && (
          <section className="bg-off-black border border-gray-800 rounded-lg p-6">
          <h2 className="text-xl font-medium mb-4">Projects</h2>
          {loadState.proj ? (
            <div className="space-y-2">
              <SkeletonBlock className="h-10 w-full" />
              <SkeletonBlock className="h-10 w-full" />
            </div>
          ) : projects.length === 0 ? (
            <p className="text-silver-medium text-sm">
              No deployments yet. Use the MCP <code className="text-silver-light">deploy</code> tool from your AI tool
              after subscribing.
            </p>
          ) : (
            <div className="overflow-x-auto -mx-1 px-1" role="region" aria-label="Deployment projects">
              <table className="w-full text-sm text-left min-w-[640px]">
                <caption className="sr-only">Your deployment projects and status</caption>
                <thead>
                  <tr className="text-silver-medium border-b border-gray-800">
                    <th scope="col" className="py-2 pr-4">
                      Slug
                    </th>
                    <th scope="col" className="py-2 pr-4">
                      Status
                    </th>
                    <th scope="col" className="py-2 pr-4">
                      Runtime
                    </th>
                    <th scope="col" className="py-2 pr-4">
                      URL
                    </th>
                    <th scope="col" className="py-2 pr-4">
                      Abuse / metrics
                    </th>
                    <th scope="col" className="py-2">
                      Updated
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {projects.map((p) => (
                    <tr key={p.slug} className="border-b border-gray-900">
                      <td className="py-2 pr-4">
                        <Link
                          to={`/limelite/deploy/projects/${encodeURIComponent(p.slug)}`}
                          className="text-silver-light hover:text-lime-400 font-mono transition-colors underline-offset-2 hover:underline"
                        >
                          {p.slug}
                        </Link>
                      </td>
                      <td className="py-2 pr-4">{p.deploymentState || p.status}</td>
                      <td className="py-2 pr-4">{p.runtime || "—"}</td>
                      <td className="py-2 pr-4 break-all max-w-xs">
                        {p.publicUrl ? (
                          <a href={p.publicUrl} className="text-blue-400 hover:underline" target="_blank" rel="noreferrer">
                            {p.publicUrl}
                          </a>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className="py-2 pr-4 align-top text-xs text-silver-medium max-w-[14rem]">
                        {p.abuseAutoStoppedAt ? (
                          <span className="block text-amber-200/95 mb-1">
                            Auto-stopped: {new Date(p.abuseAutoStoppedAt).toLocaleString()}
                            {p.abuseAutoStopReason ? ` — ${p.abuseAutoStopReason}` : ""}
                          </span>
                        ) : null}
                        <label className="flex items-center gap-2 cursor-pointer mt-1">
                          <input
                            type="checkbox"
                            checked={p.abuseMetricsSuppressed === true}
                            disabled={abusePatchSlug === p.slug}
                            onChange={(e) =>
                              void patchProjectAbuse(p.slug, { abuseMetricsSuppressed: e.target.checked })
                            }
                          />
                          Suppress anomaly stop (escape hatch)
                        </label>
                        {p.abuseAutoStoppedAt ? (
                          <button
                            type="button"
                            disabled={abusePatchSlug === p.slug}
                            onClick={() => void patchProjectAbuse(p.slug, { clearAbuseAutoStoppedAt: true })}
                            className="mt-2 text-amber-200/90 border border-amber-800/80 hover:border-amber-500 rounded px-2 py-0.5 disabled:opacity-50"
                          >
                            Clear auto-stop flag
                          </button>
                        ) : null}
                        {typeof p.abuseMetricAnomalyStreak === "number" && p.abuseMetricAnomalyStreak > 0 ? (
                          <span className="block mt-1 text-silver-medium">
                            Anomaly streak: {p.abuseMetricAnomalyStreak}
                          </span>
                        ) : null}
                      </td>
                      <td className="py-2 text-silver-medium">
                        {p.updatedAt ? new Date(p.updatedAt).toLocaleString() : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
          )}

          {/* Security — abuse protection + compliance exports */}
          {activeTab === "security" && (
          <div
            className="bg-slate-900/60 border border-slate-700/80 rounded-lg px-4 py-3 text-sm text-slate-100"
            role="region"
            aria-label="MCP abuse protection"
          >
            <p className="font-medium text-slate-50">MCP abuse protection</p>
            <p className="text-slate-200/90 mt-1">
              If your tool returns error code <code className="text-slate-100">ABUSE_SIGNAL_LIMIT</code>, we temporarily
              blocked the action because deploy activity looked unusual (too many deploys per hour, too many different
              repositories in 24h, or too many client networks in 24h). Wait and retry at a lower pace, or{" "}
              {deploySupportEmail ? (
                <a className="text-blue-400 hover:underline" href={`mailto:${deploySupportEmail}`}>
                  contact support
                </a>
              ) : (
                "contact support"
              )}{" "}
              for a higher limit. EUR billing is unchanged — this guardrail only limits risky patterns.
            </p>
            {loadState.abuseAudit ? (
              <p className="text-slate-500 text-xs mt-2">Loading recent guard events…</p>
            ) : abuseAuditEntries.length > 0 ? (
              <div className="mt-3 overflow-x-auto -mx-1 px-1">
                <p className="text-slate-400 text-xs mb-1" id="abuse-guard-caption">
                  Recent guard events (your account)
                </p>
                <table
                  className="w-full text-xs text-left border border-slate-800 rounded min-w-[520px]"
                  aria-describedby="abuse-guard-caption"
                >
                  <caption className="sr-only">Recent abuse guard events for your account</caption>
                  <thead>
                    <tr className="text-slate-500 border-b border-slate-800">
                      <th scope="col" className="py-1.5 px-2">Time (UTC)</th>
                      <th scope="col" className="py-1.5 px-2">Action</th>
                      <th scope="col" className="py-1.5 px-2">Detail</th>
                    </tr>
                  </thead>
                  <tbody>
                    {abuseAuditEntries.map((row, idx) => (
                      <tr key={`${row.action}-${idx}`} className="border-b border-slate-900/80">
                        <td className="py-1.5 px-2 text-slate-400 whitespace-nowrap">
                          {row.createdAt ? new Date(row.createdAt).toISOString() : "—"}
                        </td>
                        <td className="py-1.5 px-2 text-slate-200">{row.action}</td>
                        <td className="py-1.5 px-2 text-slate-400 font-mono">
                          {row.meta && typeof row.meta === "object" ? JSON.stringify(row.meta) : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-slate-500 text-xs mt-2">No abuse guard events recorded recently.</p>
            )}
            <div className="mt-4 pt-3 border-t border-slate-800">
              <p className="text-slate-400 text-xs mb-2">Compliance export (SOC2-friendly)</p>
              <p className="text-slate-500 text-xs mb-2">
                Download MCP and control-plane audit events for your account (UTC, last 90 days, secrets redacted in{" "}
                <code className="text-slate-400">meta_json</code>).
              </p>
              <button
                type="button"
                onClick={() => void downloadAuditCsv()}
                disabled={auditExportLoading}
                className={`text-sm border border-slate-700 hover:border-slate-500 text-slate-100 px-3 py-2 rounded-md disabled:opacity-50 ${btnFocus}`}
              >
                {auditExportLoading ? "Preparing CSV…" : "Download audit log (CSV)"}
              </button>
              <p className="text-slate-400 text-xs mt-3 mb-2">GDPR — full account export (ZIP)</p>
              <p className="text-slate-500 text-xs mb-2">
                Machine-readable JSON files: customer, projects, jobs, audit logs (redacted), and related records. API
                key ids are truncated; secrets are never stored server-side.
              </p>
              <button
                type="button"
                onClick={() => void downloadDataExportZip()}
                disabled={zipExportLoading}
                className={`text-sm border border-slate-700 hover:border-slate-500 text-slate-100 px-3 py-2 rounded-md disabled:opacity-50 ${btnFocus}`}
              >
                {zipExportLoading ? "Building ZIP…" : "Download data export (ZIP)"}
              </button>
            </div>
          </div>
          )}

        </div>

        <DeployLegalFooter />
      </div>
      </main>
    </>
  );
}

export default DeployDashboardPage;
